import { account } from '../core/account.js';
import { catalog } from '../core/catalog.js';
import { openOrderEditor } from '../ui/order-editor.js';
import { customOrder } from '../core/custom-order.js';
import { applyGroupOrder } from '../core/order-core.js';
import { device } from '../core/device.js';
import { notify } from '../core/notify.js';
import { settings } from '../core/storage.js';
import { filters } from '../core/filters.js';
import { createCountryFilter } from '../ui/country-filter.js';
import { stripCountry } from '../core/country.js';
import { t } from '../core/i18n.js';
import { debounce, normalizeText, escapeHtml } from '../core/utils.js';
import { bindVoice } from '../core/voice.js';
import { providerPickerItems } from '../core/provider-groups.js';
import { mountLibraryHero } from './hero-carousel.js';
import { createPosterStage } from '../ui/poster-stage.js';
import { movieCard, seriesCard, mediaRow, filterBar, pageHead, skeletonGrid, empty, enhance, infiniteScroll, openCategoryPicker, bindViewToggle, groupByCategory, categorySections, sortByWatched, bindExpandCards } from '../ui/render.js';

/* Listados de Películas y Series comparten la misma página parametrizada por `kind`. */

const CHUNK = 48;

const ROWS_CHUNK = 5; // filas (categorías) por tanda en la vista «Por categorías»

function sortOptions() {
    return [
        { id: 'added', name: t('movies.sortAdded') },
        { id: 'name', name: t('movies.sortName') },
        { id: 'rating', name: t('movies.sortRating') },
        { id: 'year', name: t('movies.sortYear') },
        { id: 'watched', name: t('common.sortWatched') },
        { id: 'mine', name: t('sort.mineCats') },
        { id: 'mine-edit', name: t('sort.mineEdit') }
    ];
}

function sortItems(items, sort, watchedAt) {
    const list = items.slice();
    switch (sort) {
        case 'watched': return sortByWatched(list, watchedAt);
        case 'name': return list.sort((a, b) => stripCountry(a.name).localeCompare(stripCountry(b.name)));
        case 'rating': return list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        case 'year': return list.sort((a, b) => (Number(b.year) || 0) - (Number(a.year) || 0));
        default: return list.sort((a, b) => (b.added || 0) - (a.added || 0));
    }
}

export function createLibraryPage(kind) {
    const isMovies = kind === 'vod';
    const sortKey = isMovies ? 'moviesSort' : 'seriesSort';
    return {
        path: isMovies ? '/movies/' : '/series/',
        nav: isMovies ? 'movies' : 'series',

        render() {
            return `<div class="page" data-name="${isMovies ? 'movies' : 'series'}">
                <div class="page-content">
                    <div data-hero hidden></div>
                    <div class="page-sticky">
                    ${pageHead({
                        title: isMovies ? t('movies.title') : t('series.title'),
                        bar: true,
                        actions: `<button type="button" class="btn btn-icon head-surprise-btn" data-surprise aria-label="${t('watched.surprise')}"><span class="mi">casino</span></button>`,
                        search: true,
                        searchPlaceholder: isMovies ? t('movies.search') : t('series.search')
                    })}
                    <div data-filters></div>
                    <div class="live-toolbar">
                        <div class="page-sub" data-count></div>
                        <div class="live-tools" data-nav-row>
                            <button type="button" class="btn btn-sm surprise-btn" data-surprise aria-label="${t('watched.surprise')}" title="${t('watched.surprise')}"><span class="mi">casino</span></button>
                            <div class="seg" role="group" aria-label="${t('live.viewGrid')} / ${t('live.viewList')}">
                                <button class="seg-btn ${(settings.get('libraryView') || 'rows') === 'grid' ? 'is-active' : ''}" data-view="grid" aria-label="${t('live.viewGrid')}"><span class="mi">grid_view</span></button>
                                <button class="seg-btn ${(settings.get('libraryView') || 'rows') === 'rows' ? 'is-active' : ''}" data-view="rows" aria-label="${t('live.viewRows')}"><span class="mi">view_stream</span></button>
                                <button class="seg-btn ${settings.get('libraryView') === 'list' ? 'is-active' : ''}" data-view="list" aria-label="${t('live.viewList')}"><span class="mi">view_list</span></button>
                                <button class="seg-btn ${settings.get('libraryView') === 'stage' ? 'is-active' : ''}" data-view="stage" aria-label="${t('live.viewStage')}" title="${t('live.viewStage')}"><span class="mi">smart_display</span></button>
                            </div>
                        </div>
                    </div>
                    </div>
                    <div data-list>${catalog.loaded ? '' : skeletonGrid()}</div>
                    <div data-sentinel style="height:1px"></div>
                </div>
            </div>`;
        },

        mount(page, route) {
            const root = page.el;
            bindExpandCards(root); // tarjeta desplegable de las filas por categorías
            // Estilo de la tarjeta ampliada: crece dentro de la fila (por defecto) o sale flotando encima (ajuste «Tarjeta ampliada»).
            const inRow = settings.get('peekCard') !== false && settings.get('peekStyle') !== 'float' && (device.hasHover || device.isTV || document.documentElement.classList.contains('mode-tv'));
            // Sin categoría en la URL se vuelve a lo último que eligió este espectador.
            const saved = route.query && route.query.cat ? {} : filters.get(kind);
            const routeCat = (route.query && route.query.cat) || 'all';
            const state = {
                // Ámbito (todo / Mi lista) separado de la categoría.
                scope: routeCat === 'fav' ? 'fav' : 'all',
                // `wanted` es lo elegido; `cats`, lo que de eso existe ahora mismo en el catálogo.
                // Proveedores elegidos (varios a la vez); vacío = todos. Antes se guardaba uno solo en `provider`.
                providers: Array.isArray(saved.providers) ? saved.providers.map(String) : typeof saved.provider === 'string' && saved.provider !== 'all' ? [saved.provider] : [],
                wanted: routeCat === 'fav' || routeCat === 'all' ? (saved.cats || []).map(String) : [String(routeCat)],
                cats: [],
                sort: settings.get(sortKey) || 'added',
                // Vistas: todo, solo lo que aún no has visto, o solo lo visto (películas vistas / series con todos los episodios vistos)
                seen: ['unseen', 'seen'].indexOf(saved.seen) !== -1 ? saved.seen : 'all',
                view: settings.get('libraryView') || 'rows',
                q: '',
                shown: 0,
                items: []
            };
            const els = {
                filters: root.querySelector('[data-filters]'),
                count: root.querySelector('[data-count]'),
                list: root.querySelector('[data-list]'),
                sentinel: root.querySelector('[data-sentinel]'),
                search: root.querySelector('[data-search]'),
                clear: root.querySelector('[data-search-clear]'),
                seg: root.querySelector('.seg')
            };
            const country = createCountryFilter(kind, kind, saved);
            let catList = [];
            const scopeList = () => {
                const list = [{ id: 'all', name: t('common.all'), icon: 'apps' }];
                if (catalog.favoriteItems(kind).length) list.push({ id: 'fav', name: t('nav.mylist'), icon: 'favorite' });
                return list;
            };

            // Lo que dejan pasar los filtros (sin el buscador): también lo que enseña «Ordenar a mi gusto».
            const filtered = () => {
                let items = state.scope === 'fav' ? catalog.favoriteItems(kind) : catalog.byCategories(kind, state.cats);
                if (state.scope === 'fav' && state.cats.length) items = items.filter((m) => state.cats.indexOf(String(m.categoryId)) !== -1);
                items = country.apply(items);
                if (state.providers.length) items = items.filter((m) => state.providers.some((id) => catalog.inProvider(m, id)));
                if (state.seen !== 'all') items = items.filter((m) => catalog.isWatched(isMovies ? 'vod' : 'seriesdone', m.id) === (state.seen === 'seen'));
                return items;
            };
            const compute = () => {
                let items = filtered();
                if (state.q) {
                    const q = normalizeText(state.q);
                    items = items.filter((m) => m._s.indexOf(q) !== -1);
                }
                state.items = sortItems(items, state.sort, catalog.watchedAt(kind));
            };

            const renderCats = () => {
                country.sync();
                const cats = country.categories(catalog.categories[kind]
                    .filter((c) => !(catalog.lockActive && catalog.isLockedCategory(kind, c.id))))
                    .map((c) => ({ id: c.id, name: c.name, count: c.count }));
                const extra = [];
                if (catalog.lockActive && !catalog.isKids) extra.push({ id: 'unlock', name: t('common.unlock'), icon: 'lock', exclusive: true });
                catList = [{ id: 'all', name: t('common.all'), icon: 'apps' }].concat(extra, cats);
                const scopes = scopeList();
                if (!scopes.some((x) => x.id === state.scope)) state.scope = 'all';
                const scope = scopes.find((x) => x.id === state.scope) || scopes[0];
                state.cats = state.wanted.filter((id) => cats.some((c) => String(c.id) === id));
                const cat = { name: state.cats.length === 0 ? t('common.all') : state.cats.length === 1 ? (cats.find((c) => String(c.id) === state.cats[0]) || {}).name : t('common.nCategories', { n: state.cats.length }) };
                const sort = sortOptions().find((s) => s.id === state.sort) || sortOptions()[0];
                // Sin ningún título en esta sección el combo sale aunque haya una sola fuente: así se ve cuál es y se puede añadir otra.
                const provs = catalog.providerChoices({ always: catalog.visible(kind).length === 0, kind });
                state.providers = state.providers.filter((id) => provs.some((p) => p.id === id));
                const provValue = catalog.onlyLibraries ? t('library.mine') : state.providers.length === 0 ? t('common.all') : state.providers.length === 1 ? (provs.find((p) => p.id === state.providers[0]) || {}).name : t('live.nProviders', { n: state.providers.length });
                void scope; // el ámbito solo llega por URL (?cat=fav); Mi lista tiene su propia sección
                const drawFilters = (html) => { if (device.isTV) window.IPTV.tvnav.preserve(() => { els.filters.innerHTML = html; }, els.filters); else els.filters.innerHTML = html; };
                drawFilters(filterBar([
                    country.chip(),
                    provs.length ? { key: 'provider', icon: 'dns', label: t('common.provider'), value: provValue, active: catalog.onlyLibraries || state.providers.length > 0, compactValue: true } : null,
                    { key: 'cat', icon: isMovies ? 'movie' : 'tv', label: t('common.category'), value: cat.name, active: state.cats.length > 0 },
                    { key: 'seen', icon: 'visibility', label: t('watched.filter'), value: t(state.seen === 'unseen' ? 'watched.unseen' : state.seen === 'seen' ? 'watched.seen' : 'common.all'), active: state.seen !== 'all', compactValue: true },
                    { key: 'sort', icon: 'sort', label: t('common.sort'), value: sort.name, active: state.sort !== 'added', compactValue: true }
                ].filter(Boolean)));
            };

            const heroHost = root.querySelector('[data-hero]');
            const heroApi = mountLibraryHero(heroHost, kind);
            // Barra (título, búsqueda, filtros) superpuesta a la cabecera destacada: la cabecera sube por detrás de ella.
            const content = root.querySelector('.page-content');
            const bar = root.querySelector('.page-sticky');
            const overlay = settings.get('libraryBarOverlay') === true;
            // Con cabecera destacada (y la barra debajo, no superpuesta), la imagen llega hasta arriba y el menú va transparente encima.
            const markHeroTop = () => {
                const on = !overlay && !heroHost.hidden && !!heroHost.querySelector('.hero');
                if (root.classList.contains('has-hero-top') === on) return;
                root.classList.toggle('has-hero-top', on);
                root.dispatchEvent(new Event('hero-top'));
            };
            if (typeof MutationObserver !== 'undefined') new MutationObserver(markHeroTop).observe(heroHost, { attributes: true, attributeFilter: ['hidden'], childList: true });
            let barObs = null;
            const syncOverlay = () => {
                const on = overlay && !heroHost.hidden && !!heroHost.innerHTML;
                root.classList.toggle('lib-over', on);
                if (!on) { root.classList.remove('bar-solid'); root.style.removeProperty('--lib-bar-h'); return; }
                root.style.setProperty('--lib-bar-h', bar.offsetHeight + 'px');
                root.classList.toggle('bar-solid', content.scrollTop > heroHost.offsetHeight - bar.offsetHeight * 2);
            };
            if (overlay && bar && content) {
                bar.after(heroHost);
                content.addEventListener('scroll', syncOverlay, { passive: true });
                if (typeof MutationObserver !== 'undefined') { barObs = new MutationObserver(syncOverlay); barObs.observe(heroHost, { attributes: true, attributeFilter: ['hidden'], childList: true }); }
                if (typeof ResizeObserver !== 'undefined') { const ro = new ResizeObserver(syncOverlay); ro.observe(bar); ro.observe(heroHost); const prev = barObs; barObs = { disconnect() { ro.disconnect(); if (prev) prev.disconnect(); } }; }
            }
            let stage = null; // vista «En pantalla»: imagen grande del título elegido y la hilera de carátulas debajo
            const leaveStage = () => { if (stage) { stage.destroy(); stage = null; } };
            // «Cambiar vista» / atrás: se vuelve a la vista que había antes de «En pantalla» (o a la cuadrícula).
            const exitStage = () => {
                const back = state.prevView && state.prevView !== 'stage' ? state.prevView : 'rows';
                state.view = back;
                settings.set('libraryView', back);
                els.seg.querySelectorAll('[data-view]').forEach((b) => b.classList.toggle('is-active', b.getAttribute('data-view') === back));
                renderList(true);
            };
            const renderList = (reset) => {
                compute();
                // La cabecera destacada solo se ve sin búsqueda ni filtros (y no en la vista «En pantalla», que ya es una cabecera).
                heroApi.update({ visible: !state.q && state.items.length > 0 && state.view !== 'stage', titles: state.q ? null : state.items });
                if (state.view === 'stage' && state.items.length) {
                    els.list.className = '';
                    els.count.textContent = state.items.length.toLocaleString() + ' ' + (isMovies ? t('common.movies') : t('common.series'));
                    state.shown = state.items.length; // no hay «más» que pintar al desplazar
                    if (!stage) {
                        const sc = root.querySelector('.page-content');
                        if (sc) sc.scrollTo({ top: 0 });
                        els.list.innerHTML = '';
                        stage = createPosterStage(els.list, {
                            kind,
                            onOpen: (k, id) => window.app.views.main.router.navigate(k === 'vod' ? '/movie/' + id + '/' : '/series/' + id + '/'),
                            onPlay: (k, id) => window.IPTV.actions.playFromCatalog(k, id),
                            onExit: () => exitStage()
                        });
                    }
                    stage.setItems(state.items);
                    return;
                }
                leaveStage();
                if (reset) {
                    state.shown = 0;
                    els.list.innerHTML = '';
                    els.list.className = state.view === 'list' ? 'chlist mlist' : state.view === 'rows' ? 'cat-rows' : 'grid';
                    state.groups = null;
                }
                els.count.textContent = state.items.length.toLocaleString() + ' ' + (isMovies ? t('common.movies') : t('common.series'));
                if (!state.items.length) {
                    els.list.className = '';
                    // Ninguna fuente trae este contenido (no es una búsqueda ni un filtro): se ofrece añadir un proveedor.
                    const none = !state.q && state.scope !== 'fav' && catalog.visible(kind).length === 0;
                    els.list.innerHTML = none
                        ? empty({ icon: isMovies ? 'movie' : 'tv', title: t(isMovies ? 'lib.noneMovies' : 'lib.noneSeries'), text: t(account.demo ? 'demo.noneHint' : 'lib.noneHint'), action: account.demo ? '' : `<div class="flex"><button type="button" class="btn btn-accent" data-href="/settings/?add=source" data-nav-default><span class="mi">add</span>${escapeHtml(t('settings.addProfile'))}</button></div>` })
                        : empty({ title: t('common.noResults'), text: t('common.noResultsHint') });
                    return;
                }
                if (state.view === 'rows') {
                    // Una fila horizontal por categoría (como en las plataformas): se pintan de pocas en pocas al desplazar.
                    if (!state.groups) {
                        // «Mi orden»: las hileras como las ordenó la persona; si no, por lo último visto, como siempre.
                        state.groups = state.sort === 'mine'
                            ? applyGroupOrder(groupByCategory(state.items, catalog.categories[kind], null), customOrder.get(kind))
                            : groupByCategory(state.items, catalog.categories[kind], catalog.watchedAt(kind));
                    }
                    els.list.insertAdjacentHTML('beforeend', categorySections(state.groups, { from: state.shown, count: ROWS_CHUNK, cardFor: isMovies ? (m) => movieCard(m, { expand: inRow }) : (m) => seriesCard(m, { expand: inRow }) }));
                    state.shown += ROWS_CHUNK;
                    enhance(els.list);
                    return;
                }
                const next = state.items.slice(state.shown, state.shown + CHUNK);
                state.shown += next.length;
                els.list.insertAdjacentHTML('beforeend', next.map((m) => (state.view === 'list' ? mediaRow(m, kind) : isMovies ? movieCard(m) : seriesCard(m))).join(''));
                enhance(els.list);
            };

            const refreshAll = () => {
                if (!catalog.loaded) return;
                renderCats();
                renderList(true);
            };

            els.filters.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-filter]');
                if (!btn) return;
                const key = btn.getAttribute('data-filter');
                if (key === 'scope') {
                    openCategoryPicker({
                        title: t('live.scope'),
                        list: scopeList(),
                        active: state.scope,
                        onSelect: (picked) => {
                            state.scope = picked;
                            renderCats();
                            renderList(true);
                        }
                    });
                } else if (key === 'cat') {
                    openCategoryPicker({
                        title: t('common.categories'),
                        list: catList,
                        active: state.cats,
                        multi: true,
                        onSelect: async (picked) => {
                            if (picked === 'unlock') {
                                if (await window.IPTV.actions.askUnlock()) refreshAll();
                                return;
                            }
                            state.wanted = Array.isArray(picked) ? picked.map(String) : [];
                            filters.set(kind, { cats: state.wanted });
                            renderCats();
                            renderList(true);
                            root.querySelector('.page-content').scrollTo({ top: 0 });
                        }
                    });
                } else if (key === 'provider') {
                    openCategoryPicker({
                        title: t('common.provider'),
                        list: [{ id: 'all', name: t('common.all'), icon: 'apps' }]
                            // Atajo: activa o desactiva «Solo mis bibliotecas» para toda la app (Inicio, Películas, Series y Búsqueda).
                            .concat(catalog.libraryProviderIds().length ? [{ id: 'mine', name: t('library.mine'), icon: 'video_library', exclusive: true }] : [])
                            .concat(providerPickerItems(catalog.providerChoices({ always: catalog.visible(kind).length === 0, kind })))
                            // Atajo para añadir otra fuente sin ir a Ajustes (abre el formulario completo).
                            .concat(account.demo ? [] : [{ id: '__add-provider', name: t('settings.addProfile'), icon: 'add', exclusive: true }]), // la demo pública no añade fuentes
                        multi: true,
                        active: (catalog.onlyLibraries ? ['mine'] : []).concat(state.providers),
                        onSelect: (picked) => {
                            if (picked === '__add-provider') { window.IPTV.openAddSource(); return; }
                            if (picked === 'mine') {
                                catalog.setOnlyLibraries(!catalog.onlyLibraries);
                                return;
                            }
                            state.providers = Array.isArray(picked) ? picked.filter((x) => x !== 'mine').map(String) : [];
                            filters.set(kind, { providers: state.providers });
                            renderCats();
                            renderList(true);
                            root.querySelector('.page-content').scrollTo({ top: 0 });
                        }
                    });
                } else if (key === 'country') {
                    country.open(() => {
                        renderCats();
                        renderList(true);
                        root.querySelector('.page-content').scrollTo({ top: 0 });
                    });
                } else if (key === 'seen') {
                    openCategoryPicker({
                        title: t('watched.filter'),
                        list: [{ id: 'all', name: t('common.all'), icon: 'apps' }, { id: 'unseen', name: t('watched.unseen'), icon: 'visibility_off' }, { id: 'seen', name: t('watched.seen'), icon: 'visibility' }],
                        active: state.seen,
                        onSelect: (picked) => {
                            state.seen = picked;
                            filters.set(kind, { seen: picked });
                            renderCats();
                            renderList(true);
                            root.querySelector('.page-content').scrollTo({ top: 0 });
                        }
                    });
                } else if (key === 'sort') {
                    openCategoryPicker({
                        title: t('common.sort'),
                        list: sortOptions().map((s) => ({ id: s.id, name: s.name, icon: 'sort' })),
                        active: state.sort,
                        onSelect: (picked) => {
                            if (picked === 'mine-edit') {
                                openOrderEditor({ section: kind, items: filtered, onClose: ({ changed }) => { if (changed) { state.sort = 'mine'; settings.set(sortKey, 'mine'); } state.groups = null; renderCats(); renderList(true); } });
                                return;
                            }
                            state.sort = picked;
                            settings.set(sortKey, state.sort);
                            state.groups = null;
                            renderCats();
                            renderList(true);
                        }
                    });
                }
            });
            // «Sorpréndeme»: algo al azar de lo que se está viendo en la lista (con los filtros puestos), prefiriendo lo que aún no has visto.
            root.querySelectorAll('[data-surprise]').forEach((b) => b.addEventListener('click', () => {
                const pool = state.items.filter((m) => !catalog.isWatched(isMovies ? 'vod' : 'seriesdone', m.id));
                const from = pool.length ? pool : state.items;
                if (!from.length) { notify.toast(t('watched.surpriseNone'), { icon: 'casino', duration: 2500 }); return; }
                const pick = from[Math.floor(Math.random() * from.length)];
                const go = () => window.app.views.main.router.navigate((isMovies ? '/movie/' : '/series/') + encodeURIComponent(pick.id) + '/');
                go(); // el dado se queda quieto: se abre la ficha al momento
            }));
            bindVoice(root);
            bindViewToggle(root);
            els.search.addEventListener('input', debounce(() => {
                state.q = els.search.value.trim();
                els.clear.hidden = !state.q;
                renderList(true);
            }, 250));
            els.clear.addEventListener('click', () => {
                els.search.value = '';
                state.q = '';
                els.clear.hidden = true;
                renderList(true);
                els.search.focus();
            });
            els.seg.addEventListener('click', (e) => {
                const b = e.target.closest('[data-view]');
                if (!b) return;
                const view = b.getAttribute('data-view');
                if (view === state.view) return;
                if (view === 'stage') state.prevView = state.view;
                state.view = view;
                settings.set('libraryView', view);
                els.seg.querySelectorAll('[data-view]').forEach((x) => x.classList.toggle('is-active', x === b));
                renderList(true);
            });

            const more = () => (state.view === 'stage' ? false : state.view === 'rows' ? !!state.groups && state.shown < state.groups.length : state.shown < state.items.length);
            const stopInfinite = infiniteScroll(els.sentinel, () => {
                if (catalog.loaded && more()) renderList(false);
            });
            // «Ver todo» de una fila: esa categoría sola, en cuadrícula.
            els.list.addEventListener('click', (e) => {
                const all = e.target.closest('[data-cat-all]');
                if (!all) return;
                e.preventDefault();
                state.wanted = [all.getAttribute('data-cat-all')];
                filters.set(kind, { cats: state.wanted });
                // «Ver todo» enseña esa categoría en cuadrícula solo esta vez: no cambia la vista elegida por defecto.
                state.view = 'grid';
                els.seg.querySelectorAll('[data-view]').forEach((x) => x.classList.toggle('is-active', x.getAttribute('data-view') === 'grid'));
                refreshAll();
                const sc = root.querySelector('.page-content');
                if (sc) sc.scrollTo({ top: 0 });
            });

            refreshAll();
            // Marcar o quitar de Mi lista no rehace la lista (se perdería el sitio y, en la tele, el foco).
            let lastFav = 0;
            const offFav = catalog.on('favorites', () => { lastFav = Date.now(); });
            const offChange = catalog.on('change', debounce(() => {
                heroApi.build();
                if (Date.now() - lastFav < 1500 && state.scope !== 'fav') renderCats(); else refreshAll();
            }, 200));
            const off = () => { offFav(); offChange(); };
            // Al irse de la página (la ficha de un título, otra pestaña) el tráiler de fondo se para; al volver, sigue.
            const onOut = (e) => { if (e.target === root && stage) stage.pause(); };
            const onIn = (e) => { if (e.target === root && stage) stage.resume(); };
            root.addEventListener('page:beforeout', onOut);
            root.addEventListener('page:beforein', onIn);
            return () => {
                root.removeEventListener('page:beforeout', onOut);
                root.removeEventListener('page:beforein', onIn);
                leaveStage();
                stopInfinite();
                heroApi.destroy();
                if (barObs) barObs.disconnect();
                off();
            };
        }
    };
}

export const moviesPage = createLibraryPage('vod');
export const seriesPage = createLibraryPage('series');
